import { useMemo, useState } from "react";
import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
} from "react";
import { Icon, type IconName } from "./Icon";

/* ---------- Button ---------- */
type BtnProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "secondary" | "ghost" | "danger";
  size?: "sm" | "md";
  loading?: boolean;
  icon?: IconName;
  block?: boolean;
};
export function Button({
  variant = "primary",
  size = "md",
  loading,
  icon,
  block,
  className = "",
  children,
  disabled,
  ...rest
}: BtnProps) {
  const cls = [
    "btn",
    variant !== "primary" && variant,
    size === "sm" && "sm",
    block && "block",
    className,
  ]
    .filter(Boolean)
    .join(" ");
  return (
    <button className={cls} disabled={disabled || loading} {...rest}>
      {loading ? (
        <span className="spinner" />
      ) : icon ? (
        <Icon name={icon} size={size === "sm" ? 15 : 16} />
      ) : null}
      {children}
    </button>
  );
}

/* ---------- Card ---------- */
export function Card({
  title,
  sub,
  actions,
  flat,
  children,
  className = "",
}: {
  title?: ReactNode;
  sub?: ReactNode;
  actions?: ReactNode;
  flat?: boolean;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={`card ${flat ? "flat" : ""} ${className}`}>
      {(title || actions) && (
        <div className="card-head between">
          <div>
            {title && <h2>{title}</h2>}
            {sub && <div className="sub">{sub}</div>}
          </div>
          {actions}
        </div>
      )}
      {children}
    </section>
  );
}

/* ---------- Field ---------- */
type FieldProps = {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  children: ReactNode;
};
export function Field({ label, hint, error, children }: FieldProps) {
  return (
    <label className="field">
      {label && <span className="label">{label}</span>}
      {children}
      {error ? (
        <span className="err">{error}</span>
      ) : hint ? (
        <span className="hint">{hint}</span>
      ) : null}
    </label>
  );
}

export function TextInput(props: InputHTMLAttributes<HTMLInputElement>) {
  return <input type="text" {...props} />;
}
export function Select({
  children,
  ...rest
}: SelectHTMLAttributes<HTMLSelectElement>) {
  return <select {...rest}>{children}</select>;
}

export function Toggle({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: ReactNode;
}) {
  return (
    <label className="switch">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className="track" />
      <span>{label}</span>
    </label>
  );
}

/* ---------- Badge ---------- */
export function Badge({
  tone = "info",
  dot,
  children,
}: {
  tone?: string;
  dot?: boolean;
  children: ReactNode;
}) {
  return (
    <span className={`badge ${tone}`}>
      {dot && <span className="dot" />}
      {children}
    </span>
  );
}

/* ---------- PageHeader ---------- */
export function PageHeader({
  title,
  desc,
  action,
}: {
  title: ReactNode;
  desc?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <header className="page-head">
      <div className="between">
        <h1>{title}</h1>
        {action}
      </div>
      {desc && <p className="desc">{desc}</p>}
    </header>
  );
}

/* ---------- EmptyState ---------- */
export function EmptyState({
  icon = "inbox",
  title,
  children,
  action,
}: {
  icon?: IconName;
  title: ReactNode;
  children?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="empty">
      <div className="ico">
        <Icon name={icon} size={24} />
      </div>
      <h3>{title}</h3>
      {children && <p>{children}</p>}
      {action}
    </div>
  );
}

/* ---------- Skeleton ---------- */
export function Skeleton({
  h = 16,
  w = "100%",
  style,
}: {
  h?: number;
  w?: number | string;
  style?: React.CSSProperties;
}) {
  return (
    <div
      className="skel"
      style={{ height: h, width: w, ...style }}
      aria-hidden="true"
    />
  );
}

export function SkeletonRows({ n = 3 }: { n?: number }) {
  return (
    <div className="stack">
      {Array.from({ length: n }).map((_, i) => (
        <Skeleton key={i} h={64} />
      ))}
    </div>
  );
}


/* ---------- Pager ---------- */

/**
 * Client-side pagination for a list that is already fully loaded. Returns the
 * current slice plus the props a <Pager> needs. Page snaps back to 0 whenever
 * the list length changes (filter, refetch), so you never sit on an empty page.
 */
export function usePaged<T>(items: T[], pageSize = 6) {
  const [page, setPage] = useState(0);
  const pageCount = Math.max(1, Math.ceil(items.length / pageSize));
  const clamped = Math.min(page, pageCount - 1);
  const slice = useMemo(
    () => items.slice(clamped * pageSize, clamped * pageSize + pageSize),
    [items, clamped, pageSize],
  );
  return {
    slice,
    pager: {
      page: clamped,
      pageCount,
      total: items.length,
      pageSize,
      onPrev: () => setPage((p) => Math.max(0, p - 1)),
      onNext: () => setPage((p) => Math.min(pageCount - 1, p + 1)),
    },
  };
}

export interface PagerProps {
  page: number;
  pageCount: number;
  total: number;
  pageSize: number;
  onPrev: () => void;
  onNext: () => void;
  /** Noun for the "1-6 of 20 orders" line. */
  label?: string;
}

export function Pager({
  page,
  pageCount,
  total,
  pageSize,
  onPrev,
  onNext,
  label = "items",
}: PagerProps) {
  if (pageCount <= 1) return null;
  const from = page * pageSize + 1;
  const to = Math.min(total, from + pageSize - 1);
  return (
    <div className="pager">
      <span className="muted small">
        {from}-{to} of {total} {label}
      </span>
      <div className="cluster">
        <Button
          size="sm"
          variant="ghost"
          icon="arrow-left"
          disabled={page === 0}
          onClick={onPrev}
        >
          <span className="hide-sm">Newer</span>
        </Button>
        <Button
          size="sm"
          variant="ghost"
          disabled={page >= pageCount - 1}
          onClick={onNext}
        >
          <span className="hide-sm">Older</span>{" "}
          <Icon name="arrow-right" size={15} />
        </Button>
      </div>
    </div>
  );
}
